/**
 * Plan Types
 *
 * Core type system for the signal-driven, plan-based homepage.
 * Each user gets an ordered list of PlanSteps based on their
 * (goal, gender, pathway) combination.
 */

import type { HomepageGender, HomepageGoal } from "../homepage.types";
import type { SelectedFile } from "@src/packages/document-vault/DocumentFileDropZone";

// ─── Pathway (4th personalization dimension) ───

export type Pathway = "solo" | "with-partner" | "with-donor" | "with-surrogate";

/**
 * Relationship roles that can change who owns a treatment task. A missing
 * role is intentionally represented by null at the product boundary: sex at
 * birth and a shared cycle are not authorization or participation signals.
 */
export type JourneyViewerRole =
  "primary" | "partner" | "donor" | "carrier" | "intended-parent";

// ─── Treatment Modality ───
// For goals like pregnancy where multiple treatment paths exist.
// The modality is determined by clinic recommendation or user selection.

export type TreatmentModality = "ivf" | "iui" | "medicated" | "natural" | "fet";

// ─── Lifecycle Stage (simplified from 5 to 3 + variants) ───

export type LifecycleStage =
  | "getting-started"
  | "treatment"
  | "complete"
  // Journey-specific variants
  | "waiting" // IVF/IUI: two-week wait
  | "screening" // Donation journeys
  | "matched"
  | "active-donation";

// ─── 4D State ───

export type HomepageState4D = {
  gender: HomepageGender;
  goal: HomepageGoal;
  pathway: Pathway;
  stage: LifecycleStage;
};

// ─── Journey Key ───

export type JourneyKey =
  | "trying-naturally"
  | "pregnancy-undecided"
  | "pregnancy-exploring"
  | "egg-freezing-solo"
  | "embryo-freezing-with-partner"
  | "embryo-freezing-with-donor"
  | "ivf-solo"
  | "ivf-with-partner"
  | "ivf-with-donor"
  | "ivf-with-surrogate"
  | "ivf-solo-male"
  | "ivf-with-partner-male"
  | "ivf-with-donor-male"
  | "ivf-with-surrogate-male"
  | "iui-solo"
  | "iui-with-partner"
  | "iui-with-donor"
  | "iui-with-partner-male"
  | "iui-with-donor-male"
  | "sperm-freezing-solo"
  | "egg-donation"
  | "sperm-donation";

// ─── Completion Signals ───

export type CompletionSignal =
  | { type: "manual" }
  | { type: "data-exists"; check: string }
  | { type: "test-result-exists"; testIds: string[] }
  | { type: "completion-percent"; field: string; threshold: number }
  | { type: "document-uploaded"; category: string }
  | { type: "field-set"; field: string }
  | { type: "all-substeps" }
  | { type: "external" };

// ─── Plan Actions ───

export type PlanActionScreen =
  | "AllPastCycles"
  | "ArticleDetail"
  | "AtHomeFertilityTesting"
  | "CareOverview"
  | "CostsOverviewScreen"
  | "CoverageFlow"
  | "CryoVaultScreen"
  | "CycleDetail"
  | "CycleDetailScreen"
  | "DocumentsScreen"
  | "FindCareScreen"
  | "LearnScreen"
  | "MenstrualHealthFlow"
  | "MedicalHistory"
  // Closed-cycle review. Handled by its own branch in the screen's action
  // router (it needs params.cycleId) rather than through SCREEN_MAP.
  | "PastCycleDetail"
  | "PharmacyScreen"
  | "ProgramsScreen"
  | "SupplementsScreen";

export type PlanAction = {
  label: string;
  /** Navigation target; runtime validation checks supported routes and params. */
  screen?: string;
  params?: Record<string, string> & {
    /** Treatment type to preselect in the shared start-treatment modal. */
    presetCycleType?: StartTreatmentPresetCycleType;
  };
  documentFiles?: SelectedFile[];
  url?: string; // External link
  variant?: "primary" | "secondary" | "outline";
  icon?: string; // Lucide icon name
  /** Side-effect signal (no navigation). */
  signal?:
    | "complete-step"
    | "open-insurance-modal"
    | "open-coverage-status-modal"
    | "open-start-treatment-modal"
    | "open-pregnancy-tracking-setup"
    /** Close a cycle whose ending is already recorded. */
    | "close-recorded-cycle"
    /**
     * Open the cycle a recorded result belongs to. A signal rather than a screen
     * because the concept builders hold no cycle, and a `CycleDetailScreen`
     * action without a cycleId is treated as a request to create one.
     */
    | "open-recorded-cycle"
    | "open-ai-chat"
    | "open-partner-or-donor-modal"
    | "open-document-upload"
    | "open-intent-picker";
};

/** A handoff may preselect only a treatment supported by the start modal. */
export type StartTreatmentPresetCycleType =
  | "ivf"
  | "icsi"
  | "iui"
  | "egg_freezing"
  | "embryo_freezing"
  | "sperm_freezing"
  | "fet"
  | "oi";

// ─── Substep Lifecycle ───

export type SubstepStatus =
  | "not-started"
  | "scheduled"
  | "done-awaiting-results"
  | "results-in"
  | "complete";

export type InTheMeantimeItem = {
  title: string;
  description: string;
  action?: PlanAction;
};

export type Substep = {
  id: string;
  title: string;
  education: string;
  status: SubstepStatus;
  scheduledDate?: string;
  expectedResultDate?: string;
  resultData?: unknown;
  interpretation?: string;
  inTheMeantime?: InTheMeantimeItem[];
  actions?: PlanAction[];
  /** If true, this substep belongs to the partner track */
  isPartnerTrack?: boolean;
};

// ─── Condition Modifiers ───

export type ConditionKey =
  | "pcos"
  | "endometriosis"
  | "thyroid"
  | "male-factor"
  | "recurrent-loss"
  | "diminished-reserve"
  | "age-38-plus";

export type ConditionModifier = {
  condition: ConditionKey;
  additionalEducation: string;
  additionalSubsteps?: Substep[];
  modifiedActions?: PlanAction[];
};

// ─── Optimization ───

export type EvidenceLevel = "strong" | "moderate" | "emerging";
export type ImpactLevel = "high" | "moderate" | "low";

export type OptimizationItem = {
  title: string;
  evidence: EvidenceLevel;
  impact: ImpactLevel;
  education: string;
  action?: PlanAction;
};

export type OptimizationWindow = {
  substepId: string;
  biologicalCycle: string;
  baselineAction: string;
  optimizationPeriod: string;
  retestAction: string;
  retestDate?: string;
  lifestyle: OptimizationItem[];
  supplements: OptimizationItem[];
  program?: {
    title: string;
    description: string;
    action: PlanAction;
  };
  ageGuidance: {
    bracket: string;
    range: [number, number];
    recommendation:
      "optimize-then-start" | "parallel" | "start-now-optimize-alongside";
    explanation: string;
  }[];
};

// ─── Plan Step ───

export type StepStatus = "not-started" | "in-progress" | "complete";

export type PlanStep = {
  id: string;
  title: string;
  subtitle: string;
  icon?: string; // Lucide icon name
  education: {
    context: string;
    details?: string;
    learnMoreLink?: string;
    /** Structured process steps with tappable keyword links */
    processSteps?: {
      who: string;
      description: string;
      keywords?: { term: string; articleId: string }[];
    }[];
  };
  actions: PlanAction[];
  substeps?: Substep[];
  completionSignal: CompletionSignal;
  conditionModifiers?: ConditionModifier[];
  partnerTrack?: {
    substeps: Substep[];
    education: string;
  };
  optimizationWindow?: OptimizationWindow;
  /** Age-specific education injected dynamically */
  ageSpecificContent?: {
    range: [number, number];
    content: string;
  }[];
};

// ─── Journey Definition ───

export type JourneyDefinition = {
  key: JourneyKey;
  gender: HomepageGender;
  goal: HomepageGoal;
  pathway: Pathway;
  title: string;
  description: string;
  steps: PlanStep[];
};

// ─── Timing Trade-off ───

export type TimingRecommendation =
  "optimize-then-start" | "parallel" | "start-now-optimize-alongside";

export type TimingGuidance = {
  goal: HomepageGoal;
  gender: HomepageGender;
  ageBrackets: {
    range: [number, number];
    recommendation: TimingRecommendation;
    explanation: string;
    supplementWindow: string;
  }[];
};

// ─── Emotional Context ───

export type EmotionalPhase =
  | "overwhelmed-start"
  | "testing-anxiety"
  | "waiting-limbo"
  | "treatment-intensity"
  | "tww-hope-fear"
  | "results-processing"
  | "next-cycle-decision";

export type EmotionalContext = {
  phase: EmotionalPhase;
  normalizedMessage: string;
  communityDataPoint?: string;
};
